/*globals login*/
import React, { useContext, useState, useEffect } from "react";
import NVHorizontalBarChart, {
  calcBarHeight,
  formats,
} from "common/graphs/NVHorizontalBarChart";
import styled from "styled-components";
import BqnContext from "common/BqnContext";
import { LegendItem } from "../utils";

const AllBqnsWrapper = styled.div`
  .horizontal-bars-wrapper {
    margin: 0;
  }

  h6 {
    margin: 20px 0 -20px 0;
  }

  .horizontal-bars-wrapper {
  }
`;

const UsageGraphLayout = styled.div`
  .centered {
    text-align: center;
  }
`;

const LegendsWrapper = styled.div`
  margin: 10px 40px 50px 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const getAspects = () => [
  {
    label: "User",
    color: "#006ab7",
    field: "user",
  },
  {
    label: "Nice",
    color: "#FFA500",
    field: "nice",
  },
  {
    label: "System",
    color: "#228B22",
    field: "sys",
  },
  {
    label: "IO-Wait",
    color: "#FA8072",
    field: "ioWait",
  },
  {
    label: "IRQ",
    color: "#F522F5",
    field: "irq",
  },
  {
    label: "Soft-IRQ",
    color: "#DC143C",
    field: "soft",
  },
  {
    label: "Steal",
    color: "#556B2F",
    field: "steal",
  },
  {
    label: "Guest",
    color: "#696969",
    field: "guest",
  },
  {
    label: "Idle",
    color: login.isTheme("dark") ? "#3a3a3e" : "#bcbcbc",
    field: "idle",
  },
];

const notAllCPU = ({ cpu }) => cpu !== "CPUall";
const notCPUField = ({ field }) => field !== "cpu";
const cpusUsageByAspects = (cpus = [], aspects) =>
  aspects
    .filter(notCPUField)
    .map(({ label, color, field, rounded = true }) => ({
      key: label,
      label,
      color,
      rounded,
      values: cpus.filter(notAllCPU).map(({ cpu, ...values }) => ({
        label: cpu,
        value: values[field],
      })),
    }));

const labelKeyMap = {
  User: "user",
  Nice: "nice",
  System: "sys",
  "IO-Wait": "ioWait",
  IRQ: "irq",
  "Soft-IRQ": "soft",
  Steal: "steal",
  Guest: "guest",
  Idle: "idle",
};

const getItems = (bqns, aspects, cpus) => {
  return bqns.reduce((acc, bqnId, index) => {
    if (!acc[bqnId]) {
      acc[bqnId] = aspects.map((aspect) => {
        const { label } = aspect;
        const labelKey = labelKeyMap[label];
        const values = cpus.reduce((accValues, cpuVal) => {
          const { bqn, cpu } = cpuVal;
          if (parseFloat(bqn) === parseFloat(bqnId) && !cpu.endsWith("all")) {
            accValues.push({ label: cpu, value: cpuVal[labelKey] });
          }
          return accValues;
        }, []);
        return { ...aspect, key: label, rounded: true, values };
      });
    }
    return acc;
  }, {});
};

const getFilteredResult = (result, selection) => {
  return result.filter((item) => {
    const { field } = item;
    return selection.has(field);
  });
};

const sortBqnIds = (a, b) => parseFloat(a) - parseFloat(b);

const Result = ({ cpus = [] }) => {
  const {
    clusterStatus: { mode },
    bqnId: bqnIdSel,
  } = useContext(BqnContext);

  const staticAspects = getAspects(login);
  const selectionStart = staticAspects.map(({ field }) => field);
  const [selection, setSelection] = useState(new Set(selectionStart));

  const bqnsSet = cpus.reduce((acc, { bqn }) => {
    acc.add(bqn);
    return acc;
  }, new Set([]));
  const bqns = [...bqnsSet];

  useEffect(() => {
    setSelection(new Set(selectionStart));
  }, [bqnIdSel]);

  if (mode !== "standalone") {
    const items = getItems(bqns, staticAspects, cpus);

    const handleLegendClick = (label) => {
      setSelection((prevSelection) => {
        const next = new Set(prevSelection);
        if (next.has(label)) {
          next.delete(label);
        } else {
          next.add(label);
        }
        return next;
      });
    };

    return (
      <AllBqnsWrapper>
        <LegendsWrapper>
          {staticAspects.map((aspect) => {
            const { field, color, label } = aspect;
            return (
              <LegendItem
                label={label}
                color={color}
                key={field}
                field={field}
                isSelected={selection.has(field)}
                onSelection={handleLegendClick}
              />
            );
          })}
        </LegendsWrapper>
        {[...bqns].sort(sortBqnIds).map((bqnId) => {
          if (
            bqnIdSel === "all" ||
            parseFloat(bqnIdSel) === parseFloat(bqnId)
          ) {
            return (
              <div key={`bqn-${bqnId}`}>
                <h6>{`BQN ${bqnId}`}</h6>
                <UsageGraphLayout>
                  <NVHorizontalBarChart
                    key={`horizontal-bar-chart-${bqnId}-${[...selection].join("-")}`}
                    id={`horizontal-bar-chart-${bqnId}-${[...selection].join("-")}`}
                    valueUnits=""
                    tooltipValueFormatter={(value) => `${formats.threeDecimals(value)} %`}
                    valueFormat={(value) => `${formats.noDecimals(value)} %`}
                    items={getFilteredResult(items[bqnId], selection)}
                    barHeight={calcBarHeight(cpus.length)}
                    valuePadding={30}
                    hideLegends={true}
                    animationDuration={750}
                    showRoundCorners={false} // Round corner logic does not work fine for live chart, so disable it temporarily.
                    showColors={true}
                    labelInsideBar="Idle"
                  />
                </UsageGraphLayout>
              </div>
            );
          }
          return null;
        })}
      </AllBqnsWrapper>
    );
  }

  const items = cpusUsageByAspects(cpus, staticAspects);

  return (
    <UsageGraphLayout>
      <NVHorizontalBarChart
        valueUnits=""
        tooltipValueFormatter={(value) => `${formats.threeDecimals(value)} %`}
        valueFormat={(value) => `${formats.noDecimals(value)} %`}
        items={items}
        barHeight={calcBarHeight(cpus.length)}
        showRoundCorners={false}
        animationDuration={750}
        showColors={true}
        valuePadding={30}
        labelInsideBar="Idle"
      />
    </UsageGraphLayout>
  );
};

export default Result;
